import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Heart, ChevronRight, ChevronDown, Globe, Calendar, Leaf, PartyPopper, Paperclip } from 'lucide-react';
import { WhatsappShareButton, WhatsappIcon } from 'react-share';
import { TruncatedText } from '../shared/TextUtils';

export const RestaurantCard = ({ 
  group, 
  showSeparator, 
  favorites, 
  toggleFavorite, 
  events, 
  userLocation, 
  formatDist, 
  getCommune 
}) => {
  return (
    <>
      {/* Séparateur "Restaurants fermés/Plats non communiqués" */}
      {showSeparator && (
        <div className="flex items-center gap-2 sm:gap-4 py-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
          <span className="text-white text-xs sm:text-sm font-medium px-2 sm:px-4 whitespace-nowrap">Fermés / Autres</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
        </div>
      )}
      
      <div 
        className={`relative rounded-xl p-4 shadow-lg border transition-all ${
          group.isSecondarySection 
            ? 'border-stone-300 opacity-60 hover:opacity-90' 
            : 'border-stone-300'
        }`}
        style={{
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}
      >
        {/* Cœur favori — flottant en haut à droite */}
        {group.restaurant && (
          <button 
            onClick={() => toggleFavorite(group.restaurant.id)} 
            className="absolute top-3 right-3 p-1.5 hover:scale-110 transition-transform z-10"
            data-testid={`favorite-btn-${group.restaurant.id}`}
          >
            <Heart className={`h-5 w-5 ${favorites.includes(group.restaurant.id) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-stone-300 hover:text-[#D4AF37]'}`} />
          </button>
        )}

        {/* Restaurant Section */}
        {group.restaurant && (
          <>
            {/* Restaurant Name & Actions — fond doré clair avec ombre + filet doré */}
            {/* Restaurant Name & Actions — fond blanc avec ombre + filet doré */}
            <div className="text-center mb-0 -mx-4 -mt-4 px-4 pt-4 pb-3 rounded-t-xl bg-white relative z-10" style={{ boxShadow: '0 6px 16px -2px rgba(0,0,0,0.12), 0 2px 6px -1px rgba(0,0,0,0.08)', borderBottom: '1.5px solid rgba(212,175,55,0.4)' }}>
              <div className="flex items-center justify-center">
                <Link 
                  to={`/restaurant/${group.restaurant.slug || group.restaurant.id}`}
                  className="hover:scale-105 transition-transform flex items-center gap-1"
                >
                  <h3 
                    className="text-warmgray-dark text-xl md:text-2xl font-extrabold cursor-pointer hover:text-terracotta transition-all"
                    data-testid={`restaurant-name-${group.restaurant.id}`}
                  >
                    {group.restaurant.name}
                  </h3>
                  {group.restaurant.show_as_new && group.restaurant.created_at && (() => {
                    const created = new Date(group.restaurant.created_at);
                    const now = new Date();
                    const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
                    if (diffDays <= 3) {
                      return (
                        <span className="new-badge ml-1 text-xs font-bold text-red-600" data-testid="new-badge">New</span>
                      );
                    }
                    return null;
                  })()}
                </Link>
              </div>

              {/* Voir la fiche — filets dorés, proche du nom */}
              <div className="text-center -mt-0.5 mb-3">
                <Link 
                  to={`/restaurant/${group.restaurant.slug || group.restaurant.id}`}
                  className="inline-flex items-center gap-2 text-[#8B6914] hover:text-[#D4AF37] transition-colors"
                  data-testid={`view-restaurant-${group.restaurant.id}`}
                >
                  <div className="h-px w-6 bg-gradient-to-r from-transparent to-[#8B6914]/40"></div>
                  <span className="text-xs font-medium italic">voir la fiche</span>
                  <div className="h-px w-6 bg-gradient-to-l from-transparent to-[#8B6914]/40"></div>
                </Link>
              </div>

              {/* Badge événement */}
              {events.filter(e => e.restaurant_id === group.restaurant.id).length > 0 && (
                <Link 
                  to={`/restaurant/${group.restaurant.slug || group.restaurant.id}`}
                  className="inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25 transition-colors"
                  data-testid="event-badge"
                >
                  <Calendar className="h-3 w-3 text-[#8B6914]" />
                  <span className="text-xs font-medium text-[#8B6914]">
                    {events.find(e => e.restaurant_id === group.restaurant.id).title}
                    {' — '}
                    {new Date(events.find(e => e.restaurant_id === group.restaurant.id).date + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
                  </span>
                </Link>
              )}
            
              {/* Infos pratiques — mini-capsule */}
              <div className="inline-flex items-center justify-center gap-2 mt-1 px-4 py-1.5 rounded-full text-xs flex-wrap" style={{ background: '#F0EDE8', color: '#6B6560' }}>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(group.restaurant.address)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-terracotta flex items-center gap-1 transition-colors"
                >
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="font-medium">{getCommune(group.restaurant)}</span>
                  {userLocation && (
                    <span className="text-[#8B6914] font-bold">
                      ({formatDist(userLocation.lat, userLocation.lng, group.restaurant.lat, group.restaurant.lng, group.restaurant.id)}km)
                    </span>
                  )}
                </a>
                <span className="text-warmgray/30">|</span>
                <a 
                  href={`tel:${group.restaurant.phone}`} 
                  className="hover:text-terracotta flex items-center gap-1 transition-colors whitespace-nowrap"
                >
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span>{group.restaurant.phone}</span>
                </a>
                {group.restaurant.website && (
                  <>
                    <span className="text-warmgray/30">|</span>
                    <a 
                      href={group.restaurant.website.startsWith('http') ? group.restaurant.website : `https://${group.restaurant.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="hover:text-terracotta flex items-center gap-1 transition-colors whitespace-nowrap"
                    >
                      <Globe className="h-3 w-3 flex-shrink-0" />
                      <span>Site</span>
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Dishes List — fond crème */}
            <div className="-mx-4 px-4 pt-4 pb-4 -mb-4 rounded-b-xl" style={{ background: '#F5F0E8' }}>
            <div className="space-y-2 px-1 md:px-4">
              {group.isTemporaryClosure ? (
                <div className="flex flex-col items-center py-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-orange-700 text-base font-medium">
                    Fermeture temporaire
                  </p>
                  {group.restaurant?.website && (
                    <a 
                      href={group.restaurant.website.startsWith('http') ? group.restaurant.website : `https://${group.restaurant.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 text-sm mt-1 underline hover:text-orange-800"
                    >
                      Plus d&apos;infos sur le site du restaurant
                    </a>
                  )}
                </div>
              ) : group.isOnVacation && group.vacationStart === group.vacationEnd ? (
                <div className="flex justify-center items-center py-2 bg-warmgray/10 rounded-lg border border-warmgray/20">
                  <p className="text-warmgray-dark text-base italic">Fermé</p>
                </div>
              ) : group.isOnVacation ? (
                <div className="flex justify-center items-center py-2 bg-warmgray/10 rounded-lg border border-warmgray/20">
                  <p className="text-warmgray-dark text-base italic">
                    {group.vacationReason || 'En vacances'}
                  </p>
                </div>
              ) : group.isClosed ? (
                <div className="flex justify-center items-center py-2 bg-warmgray/10 rounded-lg border border-warmgray/20">
                  <p className="text-warmgray-dark text-base italic">
                    {group.closedDayName ? `Fermé le ${group.closedDayName}` : 'Fermé'}
                  </p>
                </div>
              ) : group.dishes.length === 0 ? (
                null
              ) : (
                group.dishes.map((dish) => (
                  <DishItem key={dish.id} dish={dish} />
                ))
              )}
            </div>
            
            {/* Note menu + Partage */}
            {group.dishes.length > 0 && !group.isClosed && !group.isTemporaryClosure && !group.isOnVacation && (
              <div className="mt-3 pt-2 flex items-center justify-between">
                {group.restaurant?.menu_note ? (
                  <p className="text-xs text-warmgray/70 italic">{group.restaurant.menu_note}</p>
                ) : (
                  <span></span>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-warmgray/60">Partager</span>
                  <WhatsappShareButton
                    url={`https://aumenu.ch/restaurant/${group.restaurant.slug || group.restaurant.id}`}
                    title={group.dishes.some(d => d.is_event) 
                      ? `${group.dishes.find(d => d.is_event).name} chez ${group.restaurant.name}, ça te dit ?`
                      : `Regarde le plat du jour du ${group.restaurant.name}, ça a l'air trop bon !`
                    }
                    separator=" "
                  >
                    <WhatsappIcon size={22} round />
                  </WhatsappShareButton>
                </div>
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

// Sous-composant pour un plat individuel
const DishItem = ({ dish }) => {
  const [eventOpen, setEventOpen] = React.useState(false);
  const priceDisplay = dish.price > 0 
    ? `CHF ${Number.isInteger(dish.price) ? dish.price + '.-' : dish.price.toFixed(2)}`
    : null;

  // Événement en accordéon : titre + dérouler le menu
  if (dish.is_event) {
    return (
      <div className="bg-amber-50/80 rounded-lg p-3 shadow-sm border border-[#D4AF37]/30 hover:shadow-md transition-all">
        <button 
          onClick={(e) => { e.stopPropagation(); setEventOpen(!eventOpen); }}
          className="w-full flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            <PartyPopper className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <h4 className="text-warmgray-dark text-base font-extrabold text-left">{dish.name}</h4>
          </div>
          <div className="flex items-center gap-2">
            {priceDisplay && <span className="text-[#8B6914] font-bold text-sm whitespace-nowrap">{priceDisplay}</span>}
            <ChevronDown className={`h-4 w-4 text-warmgray transition-transform ${eventOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {eventOpen && dish.description && (
          <div className={`mt-2 pt-2 border-t border-[#D4AF37]/20 text-warmgray text-xs leading-relaxed whitespace-pre-line ${dish.is_description_centered ? 'text-center' : ''}`}>
            <TruncatedText text={dish.description} maxLength={2000} />
          </div>
        )}
        <p className="text-[#8B6914] mt-1 text-[10px] tracking-wide font-semibold">Menu spécial</p>
      </div>
    );
  }

  if (dish.is_title_centered || dish.is_centered) {
    return (
      <div className="bg-white/80 rounded-lg p-3 shadow-sm border border-warmgray/10 hover:shadow-md hover:border-terracotta/20 transition-all">
        <div>
          <div className="text-center">
            <h4 className="text-warmgray-dark text-base md:text-lg font-extrabold">
              <TruncatedText text={dish.name} />
              {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 inline ml-1" />}
              {(dish.is_menu || dish.is_event) && <PartyPopper className="h-4 w-4 text-amber-600 inline ml-1" />}
              {dish.flyer_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${dish.flyer_url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><Paperclip className="h-4 w-4 text-amber-600 inline ml-1 hover:text-amber-800" /></a>}
            </h4>
            {priceDisplay && (
              <p className="text-[#8B6914] font-bold text-sm mt-0.5">{priceDisplay}</p>
            )}
          </div>
          {dish.description && (
            <p className={`text-warmgray mt-0.5 text-xs leading-relaxed italic ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}>
              <TruncatedText text={dish.description} />
            </p>
          )}
          <DishBadge dish={dish} centered />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 rounded-lg p-3 shadow-sm border border-warmgray/10 hover:shadow-md hover:border-terracotta/20 transition-all">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-warmgray-dark text-base md:text-lg font-extrabold flex items-center gap-1.5">
            <TruncatedText text={dish.name} />
            {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 flex-shrink-0" />}
            {(dish.is_menu || dish.is_event) && <PartyPopper className="h-4 w-4 text-amber-600 flex-shrink-0" />}
            {dish.flyer_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${dish.flyer_url}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><Paperclip className="h-4 w-4 text-amber-600 flex-shrink-0 hover:text-amber-800" /></a>}
          </h4>
          {dish.description && (
            <p className={`text-warmgray mt-0.5 text-xs leading-relaxed italic ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}>
              <TruncatedText text={dish.description} />
            </p>
          )}
          <DishBadge dish={dish} />
        </div>
        {priceDisplay && (
          <div className="flex items-center shrink-0">
            <span className="text-[#8B6914] font-bold text-sm whitespace-nowrap">{priceDisplay}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Badge suggestion/plats du moment
const DishBadge = ({ dish, centered = false }) => {
  const cls = centered ? 'text-center' : '';
  if (dish.is_event) {
    return (
      <p className={`text-[#8B6914] mt-1 text-[10px] tracking-wide font-semibold ${cls}`}>
        Menu spécial
      </p>
    );
  }
  if (dish.valid_until) {
    return (
      <p className={`text-[#B8860B]/70 mt-1 text-[10px] tracking-wide ${cls}`}>
        Plat jusqu'au {new Date(dish.valid_until + 'T00:00:00').toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
      </p>
    );
  }
  if (dish.display_days && dish.display_days.length > 0) {
    // Calculer le dernier jour coché de la semaine en cours
    const today = new Date();
    const todayDay = today.getDay(); // 0=dim, 1=lun, ..., 6=sam
    const todayISO = todayDay === 0 ? 6 : todayDay - 1; // Convertir en 0=lun, 6=dim
    const maxDay = Math.max(...dish.display_days);
    const daysUntilEnd = maxDay >= todayISO ? maxDay - todayISO : 7 - todayISO + maxDay;
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + daysUntilEnd);
    return (
      <p className={`text-[#B8860B]/70 mt-1 text-[10px] tracking-wide ${cls}`}>
        Plat jusqu'au {endDate.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
      </p>
    );
  }
  if (dish.is_suggestion) {
    return <p className={`text-[#B8860B]/70 mt-1 text-[10px] tracking-wide ${cls}`}>Suggestion du moment</p>;
  }
  return null;
};
